import {
  ApplicationRef,
  ComponentRef,
  EmbeddedViewRef,
  EnvironmentInjector,
  Injectable,
  inject,
  createComponent,
  OnDestroy,
} from '@angular/core';
import { take } from 'rxjs/operators';
import {
  IframeBridgeService,
  IframeBridgeMessage,
  IframeDialog,
  SettingsBridgeActivatePayload,
  SettingsBridgeShowChangeLogPayload,
  SettingsChangeLogCfg,
  SettingsBridgeSaveDonePayload,
  SettingsBridgeDirtyPayload,
} from './iframe-bridge.service';
import { DialogService } from './dialogs/dialog.service';
import { AiFlag } from '@app/origami-api/services/config.service';
import { SettingsBridgePortletHostComponent } from '@app/pages/main/pages/settings/components/settings-bridge-portlet-host/settings-bridge-portlet-host.component';
import { SETTINGS_BRIDGE_PORTLET_IDS } from '@app/pages/main/pages/settings/components/settings-bridge-portlet-registry';
import { DialogWindowResponse } from '@app/origami-api/constants/dialog-window-response';

type BridgeDialogHandler<T = unknown> = (msg: IframeBridgeMessage<T>) => Promise<void>;

interface SignatureIssuesPayload {
  isRoomFlag?: boolean;
  data: AiFlag;
  requestId?: string | number;
}

interface RejectionIssuePayload {
  isEditMode?: boolean;
  data: unknown;
  requestId?: string | number;
}

interface SettingsUnsavedChangesPayload {
  requestId?: string | number;
  data?: unknown;
}

interface DialogResultPayload {
  requestId?: string | number;
  data?: unknown;
}

export interface SettingsBridgeContent extends OnDestroy {
  componentId: string;
  onBridgeActivate?: (payload: SettingsBridgeActivatePayload) => void | Promise<void>;
  onBridgeSave?: () => Promise<{ success: boolean; error?: string }>;
  onBridgeUndo?: () => void | Promise<void>;
  onBridgeLoadSettings?: () => void | Promise<void>;
  onBridgeDirty?: (isModified: boolean) => void;
  getSettingsChangeLogCfg?: () => SettingsChangeLogCfg | null;
}

interface SettingsBridgeOverlayState {
  bridgeSource: Window;
  componentId: string;
  componentRef: ComponentRef<SettingsBridgePortletHostComponent>;
  hostElement: HTMLDivElement;
  bridgeIframe: HTMLIFrameElement | null;
  bridgeElement: HTMLElement | null;
  bridgeContent: SettingsBridgeContent | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getNewTabFlag(data: unknown): boolean {
  if (!isRecord(data)) {
    return false;
  }
  return data.newTab === true;
}

/**
 * Root-level service that listens for `2` postMessages from ExtJS
 * iframes and opens the appropriate Angular dialog using lazy loading.
 *
 * Architecture:
 *   - Each dialog key is mapped to an async handler in `_registry`.
 *   - Handlers use dynamic `import()` so dialog components are code-split into
 *     separate chunks and only downloaded when first needed.
 *   - After the dialog closes, the result is posted back to the source window
 *     via `IframeBridgeService.sendTo()`.
 *
 * Adding a new dialog:
 *   1. Add its key constant to `IframeAutoFlag.Events` in global-event.service.ts.
 *   2. Add an entry in `_registry` below with a lazy `import()` for the component.
 *
 * Settings `BridgePortlet` overlays: register the lazy loader in
 * `settings-bridge-portlet-registry.ts` only — handlers are wired automatically.
 *
 * Must be eagerly instantiated — provided in ServicesModule.forRoot().
 */
@Injectable()
export class IframeIntegrationBridgeService {
  private readonly iframeBridgeService = inject(IframeBridgeService);
  private readonly dialogService = inject(DialogService);
  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);

  private readonly settingsBridgeOverlays = new Map<string, SettingsBridgeOverlayState>();
  private readonly sourceIds = new WeakMap<Window, number>();
  private sourceIdSeq = 0;
  private bridgeObserversInstalled = false;
  private bridgeResizeObserver: ResizeObserver | null = null;
  private bridgeMutationObserver: MutationObserver | null = null;
  private bridgeLayoutRafId: number | null = null;

  private readonly asBridgeDialogHandler = <TPayload>(
    handler: (msg: IframeBridgeMessage<TPayload>) => Promise<void>,
  ): BridgeDialogHandler => handler as BridgeDialogHandler;

  private readonly _registry: Record<string, BridgeDialogHandler> = {
    [IframeDialog.Events.signatureIssuesModal]: this.asBridgeDialogHandler<SignatureIssuesPayload>(async ({
      payload,
      source,
    }: IframeBridgeMessage<SignatureIssuesPayload>) => {
      window.focus();

      const { SignatureIssuesModalComponent } =
        await import('@app/ti-components/ai-flag/modals/signature-issues-modal/signature-issues-modal.component');

      await this.dialogService.showDialog<InstanceType<typeof SignatureIssuesModalComponent>, void>(
        SignatureIssuesModalComponent,
        (instance, close) => {
          instance.isRoomFlag = payload.isRoomFlag ?? false;
          instance.initialData = payload.data as AiFlag;

          instance.close$.pipe(take(1)).subscribe((res) => {
            const result: DialogResultPayload = {
              requestId: payload.requestId,
              data: res,
            };
            this.iframeBridgeService.sendTo(source, IframeDialog.Events.dialogResult, result);
            close();
          });
        },
      );
    }),

    [IframeDialog.Events.rejectionIssueModal]: this.asBridgeDialogHandler<RejectionIssuePayload>(async ({
      payload,
      source,
    }: IframeBridgeMessage<RejectionIssuePayload>) => {
      window.focus();

      const { RejectionIssueModalComponent } =
        await import('@app/ti-components/ai-flag/modals/rejection-issue-modal/rejection-issue-modal.component');

      await this.dialogService.showDialog<InstanceType<typeof RejectionIssueModalComponent>, void>(
        RejectionIssueModalComponent,
        (instance, close) => {
          instance.isEditMode = payload.isEditMode ?? false;
          instance.initialData = isRecord(payload.data) ? payload.data : null;

          instance.close$.pipe(take(1)).subscribe((res) => {
            const result: DialogResultPayload = {
              requestId: payload.requestId,
              data: res,
            };
            this.iframeBridgeService.sendTo(source, IframeDialog.Events.dialogResult, result);
            close();
          });
        },
      );
    }),

    [IframeDialog.Events.settingsUnsavedChanges]: this.asBridgeDialogHandler<SettingsUnsavedChangesPayload>(async ({
      payload,
      source,
    }: IframeBridgeMessage<SettingsUnsavedChangesPayload>) => {
      window.focus();

      const strings = tiStrings.modules.settings.settings;
      await this.dialogService
        .askYesNoQuestion(
          `<i style="color:var(--text-confirmation)" class="fa-solid fa-circle-question"></i> ${strings.unsavedChanges}`,
          getNewTabFlag(payload.data) ? strings.unsavedChangesDetails : strings.unsavedChangesDetails2,
          tiStrings.app.misc.yes,
          tiStrings.app.misc.no,
        )
        .then((res) => {
          this.iframeBridgeService.sendTo(
            source,
            IframeDialog.Events.settingsUnsavedChanges,
            res === DialogWindowResponse.yes,
          );
        });
    }),

    ...Object.fromEntries(
      SETTINGS_BRIDGE_PORTLET_IDS.map(
        (componentId) =>
          [
            componentId,
            async ({ source }: IframeBridgeMessage<unknown>) => {
              window.focus();
              this.renderSettingsBridgePortlet(componentId, source);
            },
          ] as const,
      ),
    ),
  };

  constructor() {
    this._subscribeAll();
    this._subscribeBridgeEvents();
  }

  registerBridgeContent(componentId: string, content: SettingsBridgeContent): void {
    const overlay = this.findOverlayByComponentId(componentId);
    if (overlay) {
      overlay.bridgeContent = content;
    }
  }

  unregisterBridgeContent(componentId: string): void {
    const overlay = this.findOverlayByComponentId(componentId);
    if (overlay) {
      overlay.bridgeContent = null;
    }
  }

  private findOverlayByComponentId(componentId: string): SettingsBridgeOverlayState | undefined {
    for (const overlay of this.settingsBridgeOverlays.values()) {
      if (overlay.componentId === componentId) {
        return overlay;
      }
    }
    return undefined;
  }

  private findOverlayKeyByComponentId(componentId: string): string | undefined {
    for (const [key, overlay] of this.settingsBridgeOverlays.entries()) {
      if (overlay.componentId === componentId) {
        return key;
      }
    }
    return undefined;
  }

  private _subscribeBridgeEvents(): void {
    this.iframeBridgeService
      .on<SettingsBridgeActivatePayload>(IframeDialog.Events.settingsBridgeActivate)
      .subscribe(({ payload }) => {
        const overlay = this.findOverlayByComponentId(payload.componentId);
        if (overlay?.bridgeContent?.onBridgeActivate) {
          overlay.bridgeContent.onBridgeActivate(payload);
        }
      });

    this.iframeBridgeService
      .on<{ componentId: string }>(IframeDialog.Events.settingsBridgeSaveRequest)
      .subscribe(async ({ payload }) => {
        const overlay = this.findOverlayByComponentId(payload.componentId);
        if (overlay?.bridgeContent?.onBridgeSave) {
          try {
            const result = await overlay.bridgeContent.onBridgeSave();
            this.iframeBridgeService.sendTo(overlay.bridgeSource, IframeDialog.Events.settingsBridgeSaveDone, {
              componentId: payload.componentId,
              success: result.success,
              error: result.error,
            } as SettingsBridgeSaveDonePayload);
          } catch (error) {
            this.iframeBridgeService.sendTo(overlay.bridgeSource, IframeDialog.Events.settingsBridgeSaveDone, {
              componentId: payload.componentId,
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error',
            } as SettingsBridgeSaveDonePayload);
          }
        }
      });

    this.iframeBridgeService
      .on<{ componentId: string }>(IframeDialog.Events.settingsBridgeUndoRequest)
      .subscribe(({ payload }) => {
        const overlay = this.findOverlayByComponentId(payload.componentId);
        if (overlay?.bridgeContent?.onBridgeUndo) {
          overlay.bridgeContent.onBridgeUndo();
        }
      });

    this.iframeBridgeService
      .on<SettingsBridgeShowChangeLogPayload>(IframeDialog.Events.settingsBridgeShowChangeLog)
      .subscribe(async ({ payload }) => {
        const overlay = this.findOverlayByComponentId(payload.componentId);
        if (!overlay) {
          return;
        }

        const changeLogCfg = overlay.bridgeContent?.getSettingsChangeLogCfg?.();
        if (!changeLogCfg) {
          return;
        }

        window.focus();

        const { SettingsChangeLogModalComponent } = await import(
          '@app/pages/main/pages/settings/modals/settings-change-log-modal/settings-change-log-modal.component'
        );

        await this.dialogService.showDialog<InstanceType<typeof SettingsChangeLogModalComponent>, void>(
          SettingsChangeLogModalComponent,
          (instance, close) => {
            instance.changeLogCfg = changeLogCfg;
            instance.close$.pipe(take(1)).subscribe(() => close());
          },
        );
      });
  }

  sendDirtyState(componentId: string, isModified: boolean): void {
    const overlay = this.findOverlayByComponentId(componentId);
    if (overlay) {
      this.iframeBridgeService.sendTo(overlay.bridgeSource, IframeDialog.Events.settingsBridgeDirty, {
        componentId,
        isModified,
      } as SettingsBridgeDirtyPayload);
    }
  }

  /**
   * Subscribe to every registered key. Each subscription is lightweight (a
   * filter on a Subject) — the actual dialog bundle is fetched lazily.
   */
  private _subscribeAll(): void {
    for (const [key, handler] of Object.entries(this._registry)) {
      this.iframeBridgeService.on(key).subscribe((msg) => handler(msg));
    }
  }

  private renderSettingsBridgePortlet(componentId: string, source: Window): void {
    const key = this.getOverlayKey(componentId, source);
    let overlay = this.settingsBridgeOverlays.get(key);
    if (!overlay) {
      overlay = this.createSettingsBridgeOverlay(componentId, source);
      this.settingsBridgeOverlays.set(key, overlay);
    }
    this.ensureBridgeObservers();
    this.scheduleBridgeLayouts();
  }

  private createSettingsBridgeOverlay(componentId: string, bridgeSource: Window): SettingsBridgeOverlayState {
    const componentRef = createComponent(SettingsBridgePortletHostComponent, {
      environmentInjector: this.environmentInjector,
    });
    componentRef.setInput('componentId', componentId);

    this.appRef.attachView(componentRef.hostView);

    const componentView = componentRef.hostView as EmbeddedViewRef<unknown>;
    const componentNode = componentView.rootNodes[0] as HTMLElement;

    const hostElement = document.createElement('div');
    hostElement.className = 'settings-bridge-portlet-overlay';
    hostElement.style.position = 'fixed';
    hostElement.style.display = 'none';
    hostElement.style.zIndex = '2';
    hostElement.style.overflowY = 'auto';

    hostElement.appendChild(componentNode);
    document.body.appendChild(hostElement);

    return {
      bridgeSource,
      componentId,
      componentRef,
      hostElement,
      bridgeIframe: null,
      bridgeElement: null,
      bridgeContent: null,
    };
  }

  private ensureBridgeObservers(): void {
    if (this.bridgeObserversInstalled) {
      this.resolveBridgeElements();
      this.refreshBridgeObserverTargets();
      return;
    }

    this.bridgeObserversInstalled = true;
    window.addEventListener('resize', this.scheduleBridgeLayouts);
    window.addEventListener('scroll', this.scheduleBridgeLayouts, true);

    if (typeof ResizeObserver !== 'undefined') {
      this.bridgeResizeObserver = new ResizeObserver(() => this.scheduleBridgeLayouts());
    }

    if (typeof MutationObserver !== 'undefined') {
      this.bridgeMutationObserver = new MutationObserver(() => this.scheduleBridgeLayouts());
    }

    this.resolveBridgeElements();
    this.refreshBridgeObserverTargets();
  }

  private refreshBridgeObserverTargets(): void {
    this.bridgeResizeObserver?.disconnect();
    this.bridgeMutationObserver?.disconnect();

    if (this.bridgeResizeObserver) {
      for (const overlay of this.settingsBridgeOverlays.values()) {
        if (overlay.bridgeIframe) {
          this.bridgeResizeObserver.observe(overlay.bridgeIframe);
        }
        if (overlay.bridgeElement) {
          this.bridgeResizeObserver.observe(overlay.bridgeElement);
        }
      }
    }

    if (this.bridgeMutationObserver) {
      for (const overlay of this.settingsBridgeOverlays.values()) {
        if (overlay.bridgeElement) {
          this.bridgeMutationObserver.observe(overlay.bridgeElement, {
            attributes: true,
            childList: true,
            subtree: true,
          });
        }
      }
    }
  }

  private destroySettingsBridgeOverlay(key: string, skipRefresh: boolean = false): void {
    const overlay = this.settingsBridgeOverlays.get(key);
    if (!overlay) {
      return;
    }

    if (overlay.bridgeContent) {
      overlay.bridgeContent.onBridgeDirty?.(false);
      overlay.bridgeContent.ngOnDestroy?.();
    }

    overlay.hostElement.remove();
    this.appRef.detachView(overlay.componentRef.hostView);
    overlay.componentRef.destroy();
    this.settingsBridgeOverlays.delete(key);

    if (this.settingsBridgeOverlays.size === 0) {
      this.teardownBridgeObservers();
      return;
    }
    if (skipRefresh) {
      return;
    }
    this.resolveBridgeElements();
    this.refreshBridgeObserverTargets();
  }

  private teardownBridgeObservers(): void {
    if (this.bridgeLayoutRafId !== null) {
      cancelAnimationFrame(this.bridgeLayoutRafId);
      this.bridgeLayoutRafId = null;
    }

    window.removeEventListener('resize', this.scheduleBridgeLayouts);
    window.removeEventListener('scroll', this.scheduleBridgeLayouts, true);

    this.bridgeResizeObserver?.disconnect();
    this.bridgeResizeObserver = null;

    this.bridgeMutationObserver?.disconnect();
    this.bridgeMutationObserver = null;

    this.bridgeObserversInstalled = false;
  }

  private readonly scheduleBridgeLayouts = (): void => {
    if (this.bridgeLayoutRafId !== null) {
      return;
    }

    this.bridgeLayoutRafId = requestAnimationFrame(() => {
      this.bridgeLayoutRafId = null;
      this.commitBridgeLayouts();
    });
  };

  private commitBridgeLayouts(): void {
    if (this.settingsBridgeOverlays.size === 0) {
      return;
    }

    this.resolveBridgeElements();
    for (const overlay of this.settingsBridgeOverlays.values()) {
      this.commitBridgeLayout(overlay);
    }
    if (this.bridgeObserversInstalled) {
      this.refreshBridgeObserverTargets();
    }
  }

  private commitBridgeLayout(overlay: SettingsBridgeOverlayState): void {
    const hostElement = overlay.hostElement;

    const iframe = overlay.bridgeIframe;
    const bridgeElement = overlay.bridgeElement;

    if (!iframe || !bridgeElement) {
      hostElement.style.display = 'none';
      return;
    }

    const iframeRect = iframe.getBoundingClientRect();
    const bridgeRect = bridgeElement.getBoundingClientRect();
    const scaleX = iframe.clientWidth > 0 ? iframeRect.width / iframe.clientWidth : 1;
    const scaleY = iframe.clientHeight > 0 ? iframeRect.height / iframe.clientHeight : 1;

    const left = iframeRect.left + bridgeRect.left * scaleX;
    const top = iframeRect.top + bridgeRect.top * scaleY;
    const width = Math.max(0, bridgeRect.width * scaleX);
    const height = Math.max(0, bridgeRect.height * scaleY);

    if (width === 0 || height === 0) {
      hostElement.style.display = 'none';
      return;
    }

    hostElement.style.left = `${left}px`;
    hostElement.style.top = `${top}px`;
    // 88 / 12 - height of ti-header + height of the tabs
    hostElement.style.clipPath = `inset(calc(${88 / 12}rem - ${top}px) 0 0 0)`;
    hostElement.style.width = `${width}px`;
    hostElement.style.height = `${height}px`;
    hostElement.style.display = 'block';
  }

  private resolveBridgeElements(): void {
    const staleKeys: string[] = [];
    for (const [key, overlay] of this.settingsBridgeOverlays) {
      const source = overlay.bridgeSource;
      if (!source || source.closed) {
        staleKeys.push(key);
        continue;
      }
      overlay.bridgeIframe = this.findIframeByContentWindow(source);
      const sourceDocument = source.document;
      const selector = this.bridgePortletSelector(overlay.componentId);
      if (sourceDocument && !sourceDocument.querySelector(selector)) {
        staleKeys.push(key);
        continue;
      }
      overlay.bridgeElement =
        ((sourceDocument ? Array.from(sourceDocument.querySelectorAll(selector)) : []).filter(
          (el) => el.getBoundingClientRect().height,
        )[0] as HTMLElement) ?? null;
    }
    for (const staleKey of staleKeys) {
      this.destroySettingsBridgeOverlay(staleKey, true);
    }
    if (staleKeys.length > 0) {
      this.refreshBridgeObserverTargets();
    }
  }

  private bridgePortletSelector(componentId: string): string {
    const escaped =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(componentId) : componentId;
    return `.ti-bridge-portlet[data-component-id="${escaped}"]`;
  }

  private findIframeByContentWindow(source: Window): HTMLIFrameElement | null {
    const iframes = Array.from(document.querySelectorAll('iframe'));

    for (const iframe of iframes) {
      if (iframe.contentWindow === source) {
        return iframe;
      }
    }

    return null;
  }

  private getSourceId(source: Window): number {
    const current = this.sourceIds.get(source);
    if (typeof current === 'number') {
      return current;
    }
    this.sourceIdSeq += 1;
    this.sourceIds.set(source, this.sourceIdSeq);
    return this.sourceIdSeq;
  }

  private getOverlayKey(componentId: string, source: Window): string {
    return `${this.getSourceId(source)}::${componentId}`;
  }
}
